import { z } from "zod";
/**
 * Who may read and who may change a folder or a contract.
 *
 * One record shape covers both resources and both kinds of principal. Keeping
 * them symmetrical is the same bet `organisation.ts` makes about folders and
 * teams: the resolver in `access.ts` is written once, and a third resource
 * type later costs a string in an enum rather than a second rule engine.
 *
 * Access is **closed by default**. A resource nothing has been granted on is
 * reachable only by its owner — whoever uploaded the contract or created the
 * folder — and by the admin team. Nothing is visible to the organisation at
 * large merely because no rule has been written yet, because the state a
 * system falls into when somebody forgets to act has to be the safe one.
 */
/**
 * The reserved team whose members may read and change everything.
 *
 * Per organisation, not per table: `ALLOWED_EMAIL_DOMAINS` admits more than
 * one domain into a deployment, and a single `admin` id would hand one
 * organisation's staff the other's contracts.
 */
export function adminTeamIdOf(orgDomain) {
    return `admin@${orgDomain.trim().toLowerCase()}`;
}
/**
 * What a grant lets someone do.
 *
 * Not a ladder. `METADATA`, `VIEW` and `EDIT` do nest, but `UPLOAD` sits
 * beside them: a person who may file contracts into a folder is not thereby
 * allowed to read what is already in it — that is the whole point of a folder
 * you can submit to. So levels are compared by what they permit
 * (`capabilitiesOf`) rather than by rank.
 */
export const PERMISSION_LEVELS = [
    "METADATA",
    "VIEW",
    "EDIT",
    "UPLOAD",
];
export const NO_CAPABILITIES = {
    canDiscover: false,
    canView: false,
    canEdit: false,
    canUpload: false,
};
export const ALL_CAPABILITIES = {
    canDiscover: true,
    canView: true,
    canEdit: true,
    canUpload: true,
};
export function capabilitiesOf(level) {
    switch (level) {
        case "METADATA":
            return { ...NO_CAPABILITIES, canDiscover: true };
        case "VIEW":
            return { ...NO_CAPABILITIES, canDiscover: true, canView: true };
        case "EDIT":
            return ALL_CAPABILITIES;
        // Enough of the folder to choose it in a picker, and nothing of what is
        // already inside it.
        case "UPLOAD":
            return { ...NO_CAPABILITIES, canDiscover: true, canUpload: true };
    }
}
/** Someone holding two grants holds whatever either of them permits. */
export function mergeCapabilities(left, right) {
    return {
        canDiscover: left.canDiscover || right.canDiscover,
        canView: left.canView || right.canView,
        canEdit: left.canEdit || right.canEdit,
        canUpload: left.canUpload || right.canUpload,
    };
}
/**
 * Levels that reach the contracts inside a folder.
 *
 * `METADATA` and `UPLOAD` describe the folder itself and stop there. Letting
 * `METADATA` flow down would mean one grant on a folder quietly exposes the
 * title of everything anybody files into it afterwards — the accident this
 * level exists to avoid, since a contract's metadata is opted into one
 * document at a time.
 */
export function flowsDownToContracts(level) {
    return level === "VIEW" || level === "EDIT";
}
/** A person by address, or a team by id. */
export const PRINCIPAL_TYPES = ["USER", "TEAM"];
export const PERMISSION_RESOURCE_TYPES = ["FOLDER", "CONTRACT"];
const TimestampSchema = z.string().datetime();
/**
 * `principalId` is an address for a USER and a team id for a TEAM. Addresses
 * are stored lowercased, for the reason every other address in this system is:
 * Google hands back whatever case was typed, and one grant that reads
 * `Me@example.com` would simply never match.
 */
export const PermissionGrantRecordSchema = z.object({
    resourceType: z.enum(PERMISSION_RESOURCE_TYPES),
    resourceId: z.string().min(1),
    principalType: z.enum(PRINCIPAL_TYPES),
    principalId: z.string().min(1),
    level: z.enum(PERMISSION_LEVELS),
    orgDomain: z.string().min(1),
    grantedBy: z.string().email(),
    grantedAt: TimestampSchema,
});
/** How a grant is addressed in a URL and in the stored sort key. */
export function principalKeyOf(principal) {
    return `${principal.principalType}#${principal.principalId}`;
}
/** Normalise before storing or comparing: only USER ids are case-folded. */
export function normalisePrincipalId(principalType, principalId) {
    return principalType === "USER"
        ? principalId.trim().toLowerCase()
        : principalId.trim();
}
//# sourceMappingURL=permission.js.map